// contexts/authcontext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../api/axios';

const AuthContext = createContext(null);

// Keys cleared together on logout and on an expired session. The axios
// interceptor clears the same three, so they must stay in sync.
const clearStoredAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
};

const storeAuth = (token, user) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  if (user?.role) localStorage.setItem('userRole', user.role);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const initializingRef = useRef(false);

  const initializeAuth = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        clearStoredAuth();
        setLoading(false);
        initializingRef.current = false;
        return;
      }

      const response = await axios.get('/check-login');

      if (response.data.success && response.data.loggedIn) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user?.role) {
          localStorage.setItem('userRole', response.data.user.role);
        }
      } else {
        clearStoredAuth();
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      clearStoredAuth();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      initializingRef.current = false;
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Shared by all three roles — only the endpoint differs.
  const performLogin = useCallback(async (endpoint, { email, password }) => {
    try {
      const response = await axios.post(endpoint, { email, password });
      const data = response.data;

      if (data.success) {
        storeAuth(data.token, data.user);
        setUser(data.user);
        setIsAuthenticated(true);
        return { success: true, user: data.user };
      }

      return { success: false, error: data.error || 'Login failed' };
    } catch (error) {
      // axios.js tags 429 responses so the UI can distinguish "wrong password"
      // from "locked out for 15 minutes".
      if (error.isRateLimited) {
        return {
          success: false,
          isRateLimited: true,
          error: error.rateLimitMessage
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  }, []);

  const clientLogin = useCallback(
    (credentials) => performLogin('/login', credentials),
    [performLogin]
  );

  const doctorLogin = useCallback(
    (credentials) => performLogin('/DocLogin', credentials),
    [performLogin]
  );

  const graphologistLogin = useCallback(
    (credentials) => performLogin('/graph/login', credentials),
    [performLogin]
  );

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post('/logout', {});
      }
    } catch (error) {
      // A failed logout call must not prevent local state from clearing.
    } finally {
      clearStoredAuth();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    clientLogin,
    // `login` kept as an alias so any other component still calling it keeps
    // working. New code should use clientLogin.
    login: clientLogin,
    doctorLogin,
    graphologistLogin,
    logout,
    setUser,
    setIsAuthenticated,
    initializeAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext };
export default AuthProvider;